// eslint-disable-next-line max-classes-per-file
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, map, mergeMap, shareReplay, switchMap, tap, timeout } from 'rxjs/operators';
import { SiteService } from '../config/site.service';
import { AuthBean } from '../models/auth.model';
import { Configuration } from '../models/configuration';
import { DataHolder } from '../models/data-holder.model';
import { HttpRequestOptions } from '../models/http-request-options.model';

const STORAGE_USER: string = 'current_user';
const STORAGE_TOKEN: string = 'current_token';

export class InvalidRolesError<T = AuthBean> extends Error {
    public authBean: T;

    constructor(authBean: T) {
        super('INVALID_ROLES');
        this.authBean = authBean;
    }
}

@Injectable({
    providedIn: 'root',
})
export class AuthenticationService<T = AuthBean> {

    public user$: Observable<T>;
    public isLoggedIn$: Observable<boolean>;

    protected readonly configuration: Configuration;
    protected token: string;
    protected subject: BehaviorSubject<T> = new BehaviorSubject<T>(null);

    constructor(
        protected myHttpClient: HttpClient,
        protected mySiteService: SiteService,
        @Optional() configuration: Configuration,
    ) {
        if (configuration != null && Object.keys(configuration).length > 0) {
            this.configuration = configuration;
        } else {
            this.configuration = new Configuration();
        }
    }

    public async init(): Promise<void> {
        this.user$ = this.subject.asObservable().pipe(distinctUntilChanged());
        this.isLoggedIn$ = this.user$.pipe(map((user: T) => !!user), distinctUntilChanged());

        const authBean: string = await this.configuration.getValue(STORAGE_USER);
        this.token = await this.configuration.getValue(STORAGE_TOKEN);

        if (authBean != null) {
            this.subject.next(JSON.parse(authBean));
        }
    }

    public initDefaultUser(authBean: T): void {
        this.subject.next(authBean);
    }

    public setRoles(roles: string[]): void {
        // Inheritors may override this function
        console.log('AuthenticationService.setRoles() - Not Implemented', roles);
    }

    public login(username: string, password: string): Observable<T> {
        const headers: HttpHeaders = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8');

        const credentials: URLSearchParams = new URLSearchParams();
        credentials.set('username', username);
        credentials.set('password', password);

        return this.myHttpClient.post<T>(this.getLoginPath(), credentials.toString(), { headers, withCredentials: true })
            .pipe(
                switchMap(async (): Promise<void> => {
                    if (this.configuration.tokenLogin) {
                        const token: string = await this.fetchToken();
                        if (token) {
                            await this.configuration.setValue(STORAGE_TOKEN, token);
                        }
                    }
                }),
                mergeMap(() => this.getStatus()),
            );
    }

    public async loginWithToken(username: string, password: string): Promise<T> {
        const token: string = await this.fetchToken(username, password);
        if (token) {
            this.token = token;
            if (this.configuration) {
                void this.configuration.setValue(STORAGE_TOKEN, token);
            } else {
                localStorage.setItem(STORAGE_TOKEN, token);
            }
        } else {
            this.token = null;
        }
        return this.getStatus(true).toPromise();
    }

    public logout(): Observable<void> {
        return this.myHttpClient.post<void>(this.getLogoutPath(), undefined, { withCredentials: true }).pipe(
            tap(() => {
                void this.configuration.setValue(STORAGE_USER, null);
                void this.configuration.setValue(STORAGE_TOKEN, null);
                this.token = null;
                this.subject.next(null);
            }),
            catchError(() => {
                void this.configuration.setValue(STORAGE_USER, null);
                void this.configuration.setValue(STORAGE_TOKEN, null);
                this.token = null;
                this.subject.next(null);
                return of(null);
            }),
        );
    }

    public async setToken(token?: string): Promise<T> {
        this.token = token;
        void this.configuration.setValue(STORAGE_TOKEN, token);
        return this.getStatus(true).toPromise();
    }

    public isLoginValid(): Observable<boolean> {
        return this.getStatus().pipe(catchError(() => of(null)), map((authBean: T) => authBean != null));
    }

    public userHasRoles(roles: string[] | string): boolean {
        return this.checkUserHasRoles(roles, this.subject.value);
    }

    public getStatus(withToken?: boolean): Observable<T> {
        const httpOptions: HttpRequestOptions = new HttpRequestOptions();
        httpOptions.withCredentials = true;
        if (this.configuration && this.configuration.tokenLogin && this.token && withToken) {
            httpOptions.headers = new HttpHeaders({
                Authorization: `Token ${this.token}`,
            });
        }
        return this.myHttpClient.get<DataHolder>(this.getSessionPath(), httpOptions).pipe(
            timeout(this.configuration && this.token && this.subject.value ? this.configuration.statusTimeout || 200 : 60000),
            shareReplay(),
            map((dataHolder: DataHolder) => {
                const authBean: T = dataHolder.data[ 0 ] as T;
                if (this.configuration.invalidRoles && !this.checkUserHasRoles(this.configuration.invalidRoles, authBean) || !this.configuration.invalidRoles) {
                    return authBean;
                } else {
                    throw new InvalidRolesError(authBean);
                }
            }),
            tap((authBean: T) => {
                const currentAuthBean: T = this.subject.value;
                if (currentAuthBean == null || JSON.stringify(currentAuthBean) !== JSON.stringify(authBean)) {
                    if (this.configuration.tokenLogin) {
                        void this.configuration.setValue(STORAGE_USER, JSON.stringify(authBean));
                    }
                    this.subject.next(authBean);
                }
            }),
            catchError((err: HttpErrorResponse | InvalidRolesError<T>) => {
                if (this.configuration.tokenLogin) {
                    if (err instanceof InvalidRolesError || err.status === 401 || err.status === 403) {
                        if (withToken) {
                            void this.configuration.setValue(STORAGE_USER, null);
                            void this.configuration.setValue(STORAGE_TOKEN, null);
                            this.subject.next(null);
                            return throwError(err);
                        } else {
                            return this.getStatus(true);
                        }
                    } else {
                        const currentAuthBean: T = this.subject.value;
                        this.subject.next(currentAuthBean);
                        return of(currentAuthBean);
                    }
                } else {
                    this.subject.next(null);
                    return throwError(err);
                }
            }),
        );
    }

    public setTokenLogin(tokenLogin: boolean): void {
        this.configuration.tokenLogin = tokenLogin;
    }

    protected getLoginPath(): string {
        let basePath: { web: string, rest: string } = { web: this.mySiteService.getConfigKey('web'), rest: null };
        if (this.configuration?.getBasePath) {
            basePath = this.configuration.getBasePath();
        }
        return `${basePath.web}j_login`;
    }

    protected getLogoutPath(): string {
        let basePath: { web: string, rest: string } = { web: this.mySiteService.getConfigKey('web'), rest: null };
        if (this.configuration?.getBasePath) {
            basePath = this.configuration.getBasePath();
        }
        return `${basePath.web}logout`;
    }

    protected getSessionPath(): string {
        let basePath: { web: string, rest: string } = { web: null, rest: this.mySiteService.getConfigKey('rest') };
        if (this.configuration?.getBasePath) {
            basePath = this.configuration.getBasePath();
        }
        return `${basePath.rest}session/status`;
    }

    protected getTokenPath(): string {
        let basePath: { web: string, rest: string } = { web: null, rest: this.mySiteService.getConfigKey('rest') };
        if (this.configuration?.getBasePath) {
            basePath = this.configuration.getBasePath();
        }
        return `${basePath.rest}api-token`;
    }

    protected fetchToken(username?: string, password?: string): Promise<string> {
        const expirationDate: Date = new Date();
        expirationDate.setFullYear(expirationDate.getFullYear() + 2);

        const tokenRequest: unknown = {};
        tokenRequest[ 'name' ] = 'mobile';
        tokenRequest[ 'expirationDate' ] = expirationDate.getTime();
        tokenRequest[ 'authorities' ] = null;
        tokenRequest[ 'singleUse' ] = false;

        if (username) {
            tokenRequest[ 'username' ] = username;
        }
        if (password) {
            tokenRequest[ 'password' ] = password;
        }

        return this.myHttpClient.post<DataHolder>(this.getTokenPath(), tokenRequest, { withCredentials: true })
            .toPromise()
            .then((result: DataHolder) => result?.data?.[ 0 ]?.[ 'token' ] ? result.data[ 0 ][ 'token' ] : null)
            .catch(() => null);
    }

    protected checkUserHasRoles(roles: string[] | string, authBean: T): boolean {
        let retVal: boolean = false;

        if (authBean == null) {
            return false;
        }
        if (roles == null || roles.length === 0) {
            return true;
        }

        const authBeanCast: AuthBean = (authBean as unknown as AuthBean);
        if (roles instanceof Array && roles.length > 0) {
            for (const role of roles) {
                if (authBeanCast.roles && authBeanCast.roles.indexOf(role) > -1 || authBeanCast.authorities && authBeanCast.authorities.indexOf(role) > -1) {
                    retVal = true;
                }
            }
            return retVal;
        } else if (!(roles instanceof Array)) {
            if (authBeanCast.roles && authBeanCast.roles.indexOf(roles) > -1 || authBeanCast.authorities && authBeanCast.authorities.indexOf(roles) > -1) {
                retVal = true;
            }
            return retVal;
        } else {
            return true;
        }
    }

}
